import { useEffect, useMemo, useRef, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCalendarAlt, faCreditCard, faUser } from "@fortawesome/free-regular-svg-icons";
import { faLock, faShieldHalved, faCircleCheck } from "@fortawesome/free-solid-svg-icons";

import { BtnSubmit } from "../../../../components/button/button-submit/button-submit";
import MaskInput from "../../../../components/inputs/mask";
import TextInput from "../../../../components/inputs/text";
import libForm from "../../../../util/libForm";
import { IndexHook } from "./hooks";

import elo from "../../../../assets/bandeiras/elo.jpg";
import hipercard from "../../../../assets/bandeiras/hipercard.jpg";
import maestro from "../../../../assets/bandeiras/maestro.jpg";
import mastercard from "../../../../assets/bandeiras/mastercard.jpg";
import visa from "../../../../assets/bandeiras/visa.jpg";
import ModalMain from "../../../../components/modal";
import OrderSummary from "../Main/order-sumary";
import Loading from "../../../../components/loading";
import { toast, ToastContainer } from "react-toastify";
import { handlePdf } from "../../../../util/reports/receipt";
import reqEmpresaFatura from "../../../../services/reqMultiTenant/reqEmpresaFatura";
import FlagsCard from "./flags";
import { BRANDS, formatCardNumberSpaces } from "../../../../util/libCard";

const CreditCardForm = ({ empresaItensDemo }) => {
	const {
		register,
		control,
		reset,
		errors,

		handleOnSubmit,
		handleSubmit,
		handlePriorStage,

		isSubmitting,
		citiesData,

		// modal confirmação
		identModal,
		handleOpenModal,
		handleCloseModalPayment,

		statusPaymentProcess, // "idle" | "pending" | "success"
		errorPaymentProcess,
		payLoadSuccessData,
	} = IndexHook();

	const [cardNumber, setCardNumber] = useState("");
	const [brand, setBrand] = useState(null);
	const [cvvLen, setCvvLen] = useState(3);
	const [clientSideError, setClientSideError] = useState("");
	const numberInputRef = useRef(null);

	// Detecta bandeira + tamanho do CVV
	useEffect(() => {
		const clean = cardNumber.replace(/\s+/g, "");
		const found = BRANDS.find((b) => b.test(clean));
		setBrand(found || null);
		setCvvLen(found?.id === "amex" ? 4 : 3); // fallback; (se quiser incluir Amex futuramente)
	}, [cardNumber]);

	// Submit guard extra (frontend)
	const onLocalSubmit = async (data, e) => {
		setClientSideError("");

		return handleOnSubmit(data, e);
	};

	/** =========================
	 *  CÁLCULOS DO PEDIDO (NOVOS)
	 *  ========================= */
	const n = (v) => Number(v ?? 0);

	const fmtBRL = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(n(v));

	// calcula total da linha com tolerância aos campos do backend
	const calcLineTotal = (it) => {
		const qtd = n(it.qtd ?? it.quantidade ?? 1);

		// Se o backend já manda o total da linha, priorize
		if (it.total_a_pagar != null) return n(it.total_a_pagar);

		// Caso contrário, tente montar:
		const unit =
			it.valor_com_desconto != null
				? n(it.valor_com_desconto)
				: it.valor_pedido != null
					? n(it.valor_pedido)
					: it.valor != null
						? n(it.valor)
						: it.preco != null
							? n(it.preco)
							: it.price != null
								? n(it.price)
								: 0;

		// Ajustes agregados (quando existirem)
		const totDesc = n(it.total_desconto);
		const totAcresc = n(it.total_acrescimo);

		// Se vierem apenas valores unitários de desconto/acréscimo
		const descUnit = n(it.desconto);
		const acrescUnit = n(it.acrescimo);

		const base = unit * (qtd || 1);

		// prefere totais, senão usa unitários
		const descontos = totDesc || descUnit * (qtd || 1);
		const acrescimos = totAcresc || acrescUnit * (qtd || 1);

		return base - descontos + acrescimos;
	};

	const totalPedido = useMemo(() => {
		const itens = Array.isArray(empresaItensDemo) ? empresaItensDemo : [];
		return itens.reduce((acc, it) => acc + calcLineTotal(it), 0);
	}, [empresaItensDemo]);

	// últimas 4 do cartão (se ainda estiver em state; não armazene no servidor)
	const last4 = useMemo(() => {
		const clean = (cardNumber || "").replace(/\D/g, "");
		return clean.slice(-4);
	}, [cardNumber]);

	// 👇 AQUI: só montar o objeto com o que já está na tela

	const fetchInvoiceId = async (id_fatura) => {
		try {
			const { data: invoiceData } = await reqEmpresaFatura.listId(id_fatura);

			if (!invoiceData) {
				throw new Error("Fatura não encontrada.");
			}

			handlePdf(invoiceData);
		} catch (error) {
			toast.error("Erro ao baixar a fatura." + (error?.message || ""));
		}
	};

	return (
		<>
			<ToastContainer />
			<form className="max-w-5xl " onSubmit={handleSubmit(onLocalSubmit)} aria-labelledby="checkout-title">
				<h1 id="checkout-title" className="sr-only">
					Finalizar pagamento
				</h1>

				<div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
					{/* Coluna principal */}
					<section className="lg:col-span-3 space-y-4">
						{/* Card de segurança + bandeiras */}
						<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
							<div className="flex flex-wrap items-center justify-between gap-2">
								<div className="flex items-center gap-2 text-emerald-700">
									<FontAwesomeIcon icon={faShieldHalved} className="h-5 w-5" />
									<span className="text-sm font-semibold">Ambiente protegido por SSL</span>
								</div>

								<div className="flex items-center mt-4">
									<FlagsCard />
								</div>
							</div>
						</div>

						{/* Card dos dados do cartão */}
						<div
							className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm
                grid grid-cols-1 sm:grid-cols-2 gap-4"
						>
							<header className="mb-2 sm:col-span-2">
								<h2 className="text-xl font-bold tracking-tight">Dados do cartão</h2>
								<p className="text-sm text-gray-500 mt-1">
									Não salvamos dados do seu cartão. Processamento via provedor certificado.
								</p>
							</header>

							{/* Nome do titular */}
							<TextInput
								label="Nome do titular"
								id="holderName"
								placeholder="Como está no cartão"
								register={register}
								errors={errors}
								onKeyDown={libForm.EnterTab}
								className="col-span-1"
								icon={faUser}
								required
								autoComplete="cc-name"
								aria-required="true"
							/>

							{/* Número do cartão */}
							<div className="col-span-1">
								<div className="relative">
									<TextInput
										label="Número do cartão"
										id="number"
										placeholder="•••• •••• •••• ••••"
										register={(name, opts) =>
											register(name, {
												...opts,
												onChange: (e) => setCardNumber(formatCardNumberSpaces(e.target.value)),
											})
										}
										errors={errors}
										onKeyDown={libForm.EnterTab}
										className="w-full"
										icon={faCreditCard}
										required
										inputRef={numberInputRef}
										value={cardNumber}
										autoComplete="cc-number"
										aria-invalid={clientSideError ? "true" : "false"}
										maxLength={22}
									/>
									<div className="absolute right-2 top-6 h-6 w-16 flex items-center justify-end pointer-events-none">
										{brand?.img && (
											<img src={brand.img} alt={brand.name} className="max-h-6 rounded" />
										)}
									</div>
								</div>

								{clientSideError && (
									<p className="mt-1 text-sm text-red-600" role="alert">
										{clientSideError}
									</p>
								)}
							</div>

							{/* Expiração */}
							<MaskInput
								label="Data de expiração"
								name="expirationDate"
								placeholder="MM/AA"
								control={control}
								errors={errors}
								required
								onKeyDown={libForm.EnterTab}
								format="##/##"
								className="col-span-1"
								icon={faCalendarAlt}
								autoComplete="cc-exp"
							/>

							{/* CVV */}
							<TextInput
								label={"Código de segurança"}
								id="ccv"
								placeholder={cvvLen === 4 ? "4 dígitos" : "3 ou 4 dígitos"}
								register={register}
								errors={errors}
								onKeyDown={libForm.EnterTab}
								className="col-span-1"
								icon={faLock}
								required
								maxLength={4}
								autoComplete="cc-csc"
								aria-describedby="cvv-help"
							/>

							{/* Aviso de segurança (linha inteira) */}
							<div className="mt-2 flex items-center gap-2 text-xs text-gray-500 flex-wrap sm:col-span-2">
								<FontAwesomeIcon icon={faLock} className="h-3.5 w-3.5 flex-shrink-0" />
								<span className="break-words">
									Seus dados são criptografados. Recomendamos usar conexão segura e manter seu
									navegador atualizado.
								</span>
							</div>

							{/* Botão (linha inteira, alinhado à direita em ≥ sm) */}
							<div className="sm:col-span-2 flex items-center justify-end mt-1">
								<BtnSubmit
									text={isSubmitting ? "Processando..." : "Finalizar pagamento"}
									className="btn btn-primary w-full sm:w-64"
									isSubmitting={isSubmitting}
									disabled={isSubmitting}
									aria-disabled={isSubmitting}
								/>
							</div>
						</div>
					</section>

					{/* Coluna resumo (confiança + pedido) */}
					<aside className="lg:col-span-2 space-y-4">
						{/* Resumo do pedido */}
						<OrderSummary itens={empresaItensDemo} />

						{/* Selo confiança */}
						<div className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
							<div className="flex items-start gap-3">
								<FontAwesomeIcon icon={faShieldHalved} className="h-5 w-5 text-emerald-700 mt-0.5" />
								<div className="text-sm text-gray-700">
									<p className="font-semibold">
										Pagamento processado com provedores certificados Asaas
									</p>
									<p className="text-gray-500 mt-1">
										Não armazenamos seu cartão. Usamos tokenização e criptografia ponta a ponta.
									</p>
								</div>
							</div>

							<div className="flex items-center mt-4">
								<FlagsCard />
							</div>
						</div>
					</aside>
				</div>
			</form>

			{/* Modal de processamento/resultado */}
			<ModalMain ident={identModal} onClose={(e) => handleCloseModalPayment(identModal)}>
				<div className="flex flex-col max-h-[80vh]">
					{statusPaymentProcess !== "success" && (
						<div className="col-span-1">
							{statusPaymentProcess === "error" ? (
								<span className="text-2xl font-bold text-gray-700 mt-1">Pendente pagamento</span>
							) : (
								<>
									<span className="text-2xl font-bold text-gray-700 mt-1">Processando pagamento</span>
									<div className="mt-8">
										<Loading mensagem="Aguarde enquanto finalizamos seu pagamento..." />
									</div>
								</>
							)}
						</div>
					)}
					<main className="mt-3">
						{!errorPaymentProcess ? (
							<>
								{statusPaymentProcess === "success" && (
									<div
										id="receipt"
										className="mt-3 rounded-2xl border border-emerald-200 bg-white shadow-sm overflow-hidden print:shadow-none"
									>
										{/* Cabeçalho */}
										<div className="bg-emerald-600 text-white px-5 py-4 flex items-center justify-between">
											<div className="flex items-center gap-3">
												<span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white/15">
													<FontAwesomeIcon icon={faCircleCheck} className="h-4 w-4" />
												</span>
												<div>
													<p className="text-xs opacity-90 leading-tight">Pagamento</p>
													<h2 className="text-lg font-semibold -mt-0.5">Aprovado</h2>
												</div>
											</div>
										</div>

										{/* Corpo */}
										<div className="px-5 py-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-700">
											{/* Empresa / Cliente */}
											<div className="space-y-2">
												<div className="flex items-center justify-between">
													<span className="text-gray-500">Estabelecimento</span>
													<strong className="text-gray-800">SZ Sistemas</strong>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-500">Titular</span>
													<strong className="text-gray-800 truncate max-w-[60%]">
														{(typeof window !== "undefined"
															? document.getElementById("holderName")?.value
															: "") || "—"}
													</strong>
												</div>
											</div>

											{/* Método / Valor */}
											<div className="space-y-2">
												<div className="flex items-center justify-between">
													<span className="text-gray-500">Método</span>
													<strong className="text-gray-800">
														{brand?.name || "Cartão"}
														{last4 ? ` •••• ${last4}` : ""}
													</strong>
												</div>
												<div className="flex items-center justify-between">
													<span className="text-gray-500">Valor</span>
													<strong className="text-emerald-700">{fmtBRL(totalPedido)}</strong>
												</div>
											</div>

											{/* Itens do pedido (resumo curto) */}
											<div className="md:col-span-2">
												<div className="rounded-xl border border-gray-200">
													<div className="px-4 py-2 border-b bg-gray-50 text-gray-600 font-medium">
														Resumo do pedido
													</div>
													<div className="divide-y">
														{Array.isArray(empresaItensDemo) &&
															empresaItensDemo.slice(0, 4).map((it, idx) => {
																const nome = it.nome ?? it.name ?? `Item ${idx + 1}`;
																const qtd = n(it.qtd ?? it.quantidade ?? 1);
																const linha = calcLineTotal(it);
																return (
																	<div
																		key={idx}
																		className="px-4 py-2 flex items-center justify-between"
																	>
																		<span className="truncate max-w-[60%]">
																			{nome}{" "}
																			<span className="text-gray-500">
																				×{qtd || 1}
																			</span>
																		</span>
																		<span className="font-medium">
																			{fmtBRL(linha)}
																		</span>
																	</div>
																);
															})}
														{Array.isArray(empresaItensDemo) &&
															empresaItensDemo.length > 4 && (
																<div className="px-4 py-2 text-xs text-gray-500">
																	+ {empresaItensDemo.length - 4} item(ns) oculto(s)
																	no resumo
																</div>
															)}
														<div className="px-4 py-3 flex items-center justify-between bg-gray-50">
															<span className="font-semibold">Total</span>
															<span className="font-semibold text-gray-900">
																{fmtBRL(totalPedido)}
															</span>
														</div>
													</div>
												</div>
											</div>
										</div>

										{/* Rodapé de ações (esconde na impressão) */}
									</div>
								)}
							</>
						) : (
							<div className="rounded-lg border border-red-200 bg-red-50 p-4">
								<h1 className="font-bold text-xl text-red-800">
									Não foi possível processar o pagamento
								</h1>
								<span className="text-red-700 text-sm">{errorPaymentProcess}</span>
							</div>
						)}
					</main>
					<div className="flex justify-end mt-4">
						{statusPaymentProcess === "success" && (
							<div className="me-4">
								<button
									type="button"
									onClick={(e) => fetchInvoiceId(payLoadSuccessData?.data?.id_fatura)}
									className="btn btn-primary w-full sm:w-auto"
									aria-label="Baixar recibo em PDF"
									title="Baixar recibo em PDF"
								>
									Baixar recibo (PDF)
								</button>
							</div>
						)}
						<button className="btn btn-primary" onClick={(e) => handleCloseModalPayment(identModal)}>
							Fechar
						</button>
					</div>
				</div>
			</ModalMain>
		</>
	);
};

export default CreditCardForm;
